import { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "react-toastify";
import homologacionApi from "../api/homologacionApi";
import { apiGetConsecutivos } from "../api/consecutivoApi";

const emptyForm = {
    id: "",
    consecutivo: "",
    Tipo: "1",
    ValorPosible: "",
    ValorData: "",
    RangoDesde: "",
    RangoHasta: "",
};

const TIPO_IGUAL = "1";
const TIPO_RANGO = "2";

export const useHomologacion = () => {
    const [valores, setValores] = useState([]);
    const [consecutivos, setConsecutivos] = useState([]);
    const [search, setSearch] = useState("");
    const [selectedConsecutivo, setSelectedConsecutivo] = useState(null);
    const [selectedId, setSelectedId] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [loading, setLoading] = useState(false);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [confirmDeleting, setConfirmDeleting] = useState(false);

    const loadValores = useCallback(async () => {
        try {
            setLoading(true);
            const res = await homologacionApi.getAll();
            setValores(res.data || []);
        } catch {
            toast.error("Error cargando los valores de homologación");
        } finally {
            setLoading(false);
        }
    }, []);

    const loadConsecutivos = useCallback(async () => {
        try {
            const res = await apiGetConsecutivos();
            setConsecutivos(res.data || []);
        } catch {
            toast.error("Error cargando consecutivos");
        }
    }, []);

    useEffect(() => {
        loadValores();
        loadConsecutivos();
    }, [loadValores, loadConsecutivos]);

    const getConsNombre = (num) => {
        const found = consecutivos.find((c) => String(c.Consecutivo) === String(num));
        return found ? found.Api : String(num);
    };

    const getTipoFijadoPorConsecutivo = useCallback(
        (cons) => {
            if (!cons) return null;
            const first = valores.find((v) => String(v.consecutivo) === String(cons));
            if (!first) return null;
            return String(first.Tipo || TIPO_IGUAL);
        },
        [valores]
    );

    const consecutivosGrouped = useMemo(() => {
        const map = new Map();
        valores.forEach((v) => {
            const key = String(v.consecutivo);
            if (!map.has(key)) {
                const tipoCons = String(v.Tipo || TIPO_IGUAL);
                map.set(key, {
                    consecutivo: v.consecutivo,
                    nombre: getConsNombre(v.consecutivo),
                    Tipo: tipoCons,
                    count: 0,
                    items: [],
                });
            }
            const g = map.get(key);
            g.count += 1;
            g.items.push(v);
        });
        return Array.from(map.values()).sort((a, b) => Number(a.consecutivo) - Number(b.consecutivo));
    }, [valores, consecutivos]);

    const filtered = useMemo(() => {
        const q = search.toLowerCase();
        if (!q) return consecutivosGrouped;
        return consecutivosGrouped.filter((g) => {
            return (
                String(g.consecutivo).includes(q) ||
                (g.nombre || "").toLowerCase().includes(q) ||
                g.items.some((it) =>
                    (it.ValorPosible || "").toLowerCase().includes(q) ||
                    (it.ValorData || "").toLowerCase().includes(q) ||
                    (it.RangoDesde || "").includes(q) ||
                    (it.RangoHasta || "").includes(q)
                )
            );
        });
    }, [consecutivosGrouped, search]);

    const currentConsecutivoConfigs = useMemo(() => {
        const cons = form.consecutivo;
        if (!cons) return [];
        return valores.filter((v) => String(v.consecutivo) === String(cons));
    }, [valores, form.consecutivo]);

    const consecutivoTipoFijado = useMemo(() => {
        return getTipoFijadoPorConsecutivo(form.consecutivo);
    }, [form.consecutivo, getTipoFijadoPorConsecutivo]);

    const aplicarTipoFijado = useCallback(
        (targetFormPatch, cons) => {
            const tipoFijado = getTipoFijadoPorConsecutivo(cons);
            if (!tipoFijado) return targetFormPatch;
            const Tipo = tipoFijado;
            return {
                ...targetFormPatch,
                Tipo,
                ValorPosible: Tipo === TIPO_RANGO ? "" : targetFormPatch.ValorPosible || "",
                RangoDesde: Tipo === TIPO_IGUAL ? "" : targetFormPatch.RangoDesde || "",
                RangoHasta: Tipo === TIPO_IGUAL ? "" : targetFormPatch.RangoHasta || "",
            };
        },
        [getTipoFijadoPorConsecutivo]
    );

    const handleSelectGroup = (group) => {
        setSelectedConsecutivo(group.consecutivo);
        const tipoFijado = getTipoFijadoPorConsecutivo(group.consecutivo);
        const first = group.items[0];
        setSelectedId(null);
        const baseEmpty = {
            ...emptyForm,
            consecutivo: String(group.consecutivo),
            Tipo: tipoFijado || emptyForm.Tipo,
        };
        setForm(baseEmpty);
        if (first) {
            setSelectedId(first.id);
            setForm({
                id: first.id,
                consecutivo: String(first.consecutivo),
                Tipo: first.Tipo || tipoFijado || TIPO_IGUAL,
                ValorPosible: first.ValorPosible || "",
                ValorData: first.ValorData || "",
                RangoDesde: first.RangoDesde || "",
                RangoHasta: first.RangoHasta || "",
            });
        }
    };

    const handleSelectConfigItem = (item) => {
        setSelectedId(item.id);
        setForm({
            id: item.id,
            consecutivo: String(item.consecutivo),
            Tipo: item.Tipo || TIPO_IGUAL,
            ValorPosible: item.ValorPosible || "",
            ValorData: item.ValorData || "",
            RangoDesde: item.RangoDesde || "",
            RangoHasta: item.RangoHasta || "",
        });
    };

    const handleNew = () => {
        setSelectedId(null);
        setSelectedConsecutivo(null);
        const prevCons = form.consecutivo || "";
        setForm({
            ...emptyForm,
            consecutivo: prevCons,
            Tipo: emptyForm.Tipo,
        });
    };

    const saveValor = async () => {
        if (!form.consecutivo || isNaN(Number(form.consecutivo))) {
            toast.warn("El consecutivo es requerido y debe ser numérico");
            return;
        }

        const Tipo = String(form.Tipo || TIPO_IGUAL);

        if (Tipo === TIPO_IGUAL) {
            if (!form.ValorPosible.trim()) {
                toast.warn("El campo Valor Posible es requerido");
                return;
            }
        }
        if (Tipo === TIPO_RANGO) {
            if (form.RangoDesde === "" || isNaN(Number(form.RangoDesde))) {
                toast.warn("Rango Desde es requerido y debe ser numérico");
                return;
            }
            if (form.RangoHasta === "" || isNaN(Number(form.RangoHasta))) {
                toast.warn("Rango Hasta es requerido y debe ser numérico");
                return;
            }
            if (Number(form.RangoDesde) >= Number(form.RangoHasta)) {
                toast.warn("Rango Desde debe ser menor que Rango Hasta");
                return;
            }
        }
        if (!form.ValorData.trim()) {
            toast.warn("El campo Valor Data es requerido");
            return;
        }

        try {
            const payload = {
                ...form,
                consecutivo: Number(form.consecutivo),
                Tipo,
                ValorPosible: Tipo === TIPO_RANGO ? "" : String(form.ValorPosible || ""),
                RangoDesde: Tipo === TIPO_RANGO ? String(form.RangoDesde) : "",
                RangoHasta: Tipo === TIPO_RANGO ? String(form.RangoHasta) : "",
            };
            const res = await homologacionApi.save(payload);
            if (res.success) {
                toast.success(res.message || "Guardado correctamente");
                loadValores();
                setSelectedId(null);
                setForm((prev) => ({
                    ...emptyForm,
                    consecutivo: prev.consecutivo,
                    Tipo: emptyForm.Tipo,
                }));
            } else {
                toast.error(res.message || "Error al guardar");
            }
        } catch {
            toast.error("Error al guardar el registro");
        }
    };

    const requestDeleteValor = () => {
        if (!form.id) return;
        setConfirmDeleteOpen(true);
    };

    const closeDeleteConfirm = () => {
        if (confirmDeleting) return;
        setConfirmDeleteOpen(false);
    };

    const deleteValor = async () => {
        if (!form.id) return;
        try {
            setConfirmDeleting(true);
            const res = await homologacionApi.delete(form.id);
            if (res.success) {
                toast.success(res.message || "Eliminado correctamente");
                loadValores();
                const prevCons = form.consecutivo;
                setSelectedId(null);
                setForm({ ...emptyForm, consecutivo: prevCons });
                setConfirmDeleteOpen(false);
            } else {
                toast.error(res.message || "Error al eliminar");
            }
        } catch {
            toast.error("Error al eliminar el registro");
        } finally {
            setConfirmDeleting(false);
        }
    };

    return {
        valores,
        consecutivos,
        filtered,
        search,
        setSearch,
        selectedId,
        selectedConsecutivo,
        form,
        setForm,
        loading,
        handleSelectGroup,
        handleSelectConfigItem,
        handleNew,
        saveValor,
        deleteValor,
        requestDeleteValor,
        confirmDeleteOpen,
        confirmDeleting,
        closeDeleteConfirm,
        getConsNombre,
        currentConsecutivoConfigs,
        consecutivoTipoFijado,
        TIPO_IGUAL,
        TIPO_RANGO,
    };
};
